import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type AdminGrant, grantState } from '../lib/adminGrants';
import { absolute } from '../lib/time';
import { DataTable, type DataTableColumn } from './DataTable';
import { StatusPill } from './StatusPill';
import { Button } from './ui/Button';

// One subject's grant HISTORY as a dense data table (spec §3.15). Presenter —
// the panel owns the read, the busy map and the confirm.
//
// THE HISTORY INCLUDES WHAT HAS STOPPED, and that is the point: a revoke PARKS
// the row rather than deleting it, so an active-only listing would make an
// operator's own write vanish the instant they made it and leave the parked
// evidence with no reader anywhere.
//
// LIVENESS IS THE SERVER'S ANSWER. `active` is resolved for every row at ONE
// frozen instant; this file only splits the two ways a grant can be inactive —
// `revoked_iso` means a human pulled it, its absence means the expiry passed —
// and never re-derives the expiry rule against a browser clock.
//
// REVOKE IS OFFERED ON A LIVE GRANT ONLY. Revoking a stopped one is a benign
// server-side no-op (`revoked: false`), so a button there could only ever spend
// a round trip to change nothing.

export function AdminGrantsTable(props: {
  readonly rows: readonly AdminGrant[];
  readonly isBusy: (id: string) => boolean;
  readonly onRevoke: (grant: AdminGrant) => void;
}): JSX.Element {
  const columns: readonly DataTableColumn<AdminGrant>[] = [
    {
      key: 'kind',
      header: t('admin.grants_col_kind'),
      cell: (grant) => (
        <>
          <span class="data-table__mono">{grant.kind}</span>
          <Show when={grant.amount !== null}>
            <span class="admin-grants__meta">
              {t('admin.grants_amount_value', { amount: String(grant.amount) })}
            </span>
          </Show>
        </>
      ),
    },
    {
      key: 'reason',
      header: t('admin.grants_col_reason'),
      cell: (grant) => (
        <>
          <p class="admin-grants__reason">{grant.reason}</p>
          <span class="admin-grants__meta">
            {t('admin.grants_source', { source: grant.source })}
          </span>
        </>
      ),
    },
    {
      key: 'state',
      header: t('admin.grants_col_state'),
      cell: (grant) => (
        <StatusPill tone={grantState(grant).tone}>{t(grantState(grant).key)}</StatusPill>
      ),
    },
    {
      key: 'lifetime',
      header: t('admin.grants_col_lifetime'),
      cell: (grant) => (
        <>
          <span class="admin-grants__meta">
            {t('admin.grants_issued_at', { at: absolute(grant.created_iso) })}
          </span>
          <span class="admin-grants__meta">
            {grant.revoked_iso !== null
              ? t('admin.grants_revoked_at', { at: absolute(grant.revoked_iso) })
              : grant.expires_iso !== null
                ? t('admin.grants_expires_at', { at: absolute(grant.expires_iso) })
                : t('admin.grants_open_ended')}
          </span>
        </>
      ),
    },
    {
      key: 'actions',
      header: t('admin.col_actions'),
      align: 'end',
      cell: (grant) => (
        <Show when={grant.active}>
          <div class="data-table__actions">
            <Button
              variant="destructive"
              size="sm"
              disabled={props.isBusy(grant.id)}
              aria-label={t('admin.grants_revoke_label', { kind: grant.kind })}
              onClick={() => props.onRevoke(grant)}
            >
              {t('admin.grants_revoke')}
            </Button>
          </div>
        </Show>
      ),
    },
  ];

  return (
    <DataTable
      label={t('admin.grants_table_label')}
      columns={columns}
      rows={props.rows}
      rowKey={(grant) => grant.id}
    />
  );
}
